import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Images, Loader2 } from "lucide-react";

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [album, setAlbum] = useState("All");
  const [selected, setSelected] = useState(null);
  const { t, tr } = useLang();

  useEffect(() => {
    api.get("/public/gallery").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const albums = ["All", ...Array.from(new Set(items.map((i) => i.album).filter(Boolean)))];
  const filtered = album === "All" ? items : items.filter((i) => i.album === album);

  return (
    <div data-testid="public-gallery-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Moments of Seva")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("Gallery")}</h1>
          <p className="text-white/70 mt-3 max-w-xl">{t("Glimpses from yoga camps, welfare drives, festivals and ashram life.")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#5C7264]"><Images className="h-12 w-12 mx-auto mb-4 text-[#CBD2CC]" /><p>{t("No images published yet.")}</p></div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-8">
              {albums.map((a) => (
                <button key={a} onClick={() => setAlbum(a)} data-testid={`gallery-album-${a}`} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${album === a ? "bg-[#194324] text-white" : "bg-white border border-[#E2DAD0] text-[#3A4E41] hover:border-[#C59B27]"}`}>{t(a)}</button>
              ))}
            </div>
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
              {filtered.map((g) => (
                <button key={g.id} onClick={() => setSelected(g)} className="block w-full break-inside-avoid rounded-xl overflow-hidden group relative" data-testid={`public-gallery-${g.id}`}>
                  <img src={fileUrl(g.image_path)} alt={g.title} className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <div className="text-white text-left"><div className="font-medium text-sm">{tr(g, "title")}</div></div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {selected && (
            <>
              <img src={fileUrl(selected.image_path)} alt={selected.title} className="w-full max-h-[70vh] object-contain bg-black" />
              <div className="p-5">
                <div className="text-xs font-mono text-[#B38728] uppercase tracking-widest">{t(selected.album)}</div>
                <h3 className="font-display text-xl font-semibold text-[#194324] mt-1">{tr(selected, "title")}</h3>
                {selected.caption && <p className="text-[#3A4E41] mt-1">{tr(selected, "caption")}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
